import { Button, DataTable } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';
import { useCatalogos } from '../hooks/useCatalogos';
import { medicosAPI } from '../../../core/api/api';
import { ToastContainer } from '../../../shared/components';
import { ArrowBack, ChevronLeft, ChevronRight, Today, Block, CheckCircle } from '@mui/icons-material';

const NOMBRES_DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Formatea un Date a 'yyyy-MM-dd' en hora local (sin desfase de zona horaria).
const aFechaISO = (fecha) => {
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, '0');
  const d = String(fecha.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Muestra 'dd/MM/yyyy' a partir de 'yyyy-MM-dd'.
const aFechaLegible = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const DisponibilidadSemanal = ({ onVolver }) => {
  const { centros, tecnicos, loading: loadingCatalogos } = useCatalogos();

  const [idMedico, setIdMedico] = useState('');
  const [idCentro, setIdCentro] = useState('');
  const [fechaSemana, setFechaSemana] = useState(() => new Date());
  const [dias, setDias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesandoFecha, setProcesandoFecha] = useState(null);
  const [toast, setToast] = useState(null);
  const [consultado, setConsultado] = useState(false);

  const cargarSemana = useCallback(async (medico, centro, fecha) => {
    if (!medico || !centro) return;
    setLoading(true);
    try {
      const { data } = await medicosAPI.obtenerSemanaProfesional(medico, centro, aFechaISO(fecha));
      setDias(Array.isArray(data) ? data : []);
      setConsultado(true);
    } catch {
      setToast({ message: 'No se pudo cargar la disponibilidad de la semana.', type: 'error' });
      setDias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConsultar = () => {
    if (!idMedico) {
      setToast({ message: 'Elegí un técnico.', type: 'error' });
      return;
    }
    if (!idCentro) {
      setToast({ message: 'Elegí un centro.', type: 'error' });
      return;
    }
    cargarSemana(idMedico, idCentro, fechaSemana);
  };

  const cambiarSemana = (deltaDias) => {
    const nueva = new Date(fechaSemana);
    nueva.setDate(nueva.getDate() + deltaDias);
    setFechaSemana(nueva);
    if (idMedico && idCentro) cargarSemana(idMedico, idCentro, nueva);
  };

  const irHoy = () => {
    const hoy = new Date();
    setFechaSemana(hoy);
    if (idMedico && idCentro) cargarSemana(idMedico, idCentro, hoy);
  };

  const toggleDia = async (dia) => {
    setProcesandoFecha(dia.fecha);
    try {
      if (dia.deshabilitado) {
        await medicosAPI.rehabilitarDia(idMedico, idCentro, dia.fecha);
        setToast({ message: `Día ${aFechaLegible(dia.fecha)} rehabilitado.`, type: 'success' });
      } else {
        await medicosAPI.deshabilitarDia(idMedico, idCentro, dia.fecha);
        setToast({ message: `Día ${aFechaLegible(dia.fecha)} deshabilitado.`, type: 'success' });
      }
      await cargarSemana(idMedico, idCentro, fechaSemana);
    } catch {
      setToast({ message: 'No se pudo actualizar el día. Intentá de nuevo.', type: 'error' });
    } finally {
      setProcesandoFecha(null);
    }
  };

  return (
    <>
      <ToastContainer toast={toast} onClose={() => setToast(null)} />
      <div className="panel panel-info">
        <div className="panel-heading">Disponibilidad por semana</div>
        <div className="panel-body">
          <p style={{ color: '#555', marginBottom: '15px' }}>
            Elegí un técnico y un centro para ver su disponibilidad de la semana. Podés deshabilitar
            un día puntual (por ejemplo, si ese día no va a atender) y volver a habilitarlo cuando quieras.
            Esto no afecta las demás semanas.
          </p>

          <div className="row" style={{ marginBottom: '15px' }}>
            <div className="col-sm-5">
              <label>Técnico</label>
              <select className="form-control" value={idMedico} onChange={(e) => setIdMedico(e.target.value)} disabled={loadingCatalogos}>
                <option value="">Seleccione...</option>
                {tecnicos?.map((t) => <option key={t.idMedico} value={t.idMedico}>{t.medico1}</option>)}
              </select>
            </div>
            <div className="col-sm-5">
              <label>Centro</label>
              <select className="form-control" value={idCentro} onChange={(e) => setIdCentro(e.target.value)} disabled={loadingCatalogos}>
                <option value="">Seleccione...</option>
                {centros?.map((c) => <option key={c.idCentro} value={c.idCentro}>{c.centro1}</option>)}
              </select>
            </div>
            <div className="col-sm-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={handleConsultar} disabled={loading}>
                Consultar
              </Button>
            </div>
          </div>

          {consultado && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <Button type="button" className="btn btn-default" onClick={() => cambiarSemana(-7)} disabled={loading}>
                  <ChevronLeft sx={{ fontSize: 18 }} /> Semana anterior
                </Button>
                <Button type="button" className="btn btn-default" onClick={irHoy} disabled={loading}>
                  <Today sx={{ fontSize: 18, mr: 0.5 }} /> Hoy
                </Button>
                <Button type="button" className="btn btn-default" onClick={() => cambiarSemana(7)} disabled={loading}>
                  Semana siguiente <ChevronRight sx={{ fontSize: 18 }} />
                </Button>
                {loading && <span style={{ color: '#888' }}>Cargando...</span>}
              </div>

              <div className="table-container">
                <DataTable className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Día</th>
                      <th>Fecha</th>
                      <th>Atiende</th>
                      <th>Estado</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dias.map((dia) => (
                      <tr key={dia.fecha}>
                        <td>{NOMBRES_DIAS[dia.diaSemana]}</td>
                        <td>{aFechaLegible(dia.fecha)}</td>
                        <td>{dia.atiende ? 'Sí' : 'No'}</td>
                        <td>
                          {dia.deshabilitado ? (
                            <span style={{ color: '#b71c1c', fontWeight: 600 }}>Deshabilitado</span>
                          ) : dia.atiende ? (
                            <span style={{ color: '#1b5e20', fontWeight: 600 }}>Disponible</span>
                          ) : (
                            <span style={{ color: '#888' }}>Sin horario</span>
                          )}
                        </td>
                        <td>
                          {dia.atiende ? (
                            dia.deshabilitado ? (
                              <Button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => toggleDia(dia)}
                                disabled={procesandoFecha === dia.fecha}
                              >
                                <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />
                                {procesandoFecha === dia.fecha ? 'Guardando...' : 'Habilitar'}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => toggleDia(dia)}
                                disabled={procesandoFecha === dia.fecha}
                              >
                                <Block sx={{ fontSize: 16, mr: 0.5 }} />
                                {procesandoFecha === dia.fecha ? 'Guardando...' : 'Deshabilitar'}
                              </Button>
                            )
                          ) : (
                            <span style={{ color: '#bbb' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>
            </>
          )}

          <div className="form-actions" style={{ marginTop: '15px' }}>
            <Button type="button" className="btn btn-default" onClick={onVolver}>
              <ArrowBack sx={{ fontSize: 18, mr: 0.5 }} /> Volver al calendario
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DisponibilidadSemanal;
